import { COLORS } from '../theme';
import React, { useEffect, useRef } from 'react';
import { Icon, type IconKey } from '../icons';

export type IncidentType = 'Threat' | 'Medical' | 'Infrastructure' | 'Environmental' | 'Unknown';

export interface IncidentDraft {
  type: IncidentType;
  severity: 1 | 2 | 3 | 4 | 5;
  title: string;
  description: string;
  location_label: string;
}

interface IncidentCreateModalProps {
  lat: number;
  lng: number;
  draft: IncidentDraft;
  setDraft: React.Dispatch<React.SetStateAction<IncidentDraft>>;
  onSubmit: () => void;
  onClose: () => void;
  submitting: boolean;
}

const INCIDENT_TYPES: { value: IncidentType; label: string; color: string; icon: IconKey }[] = [
  { value: 'Threat', label: 'Threat', color: '#ff4444', icon: 'threat' },
  { value: 'Medical', label: 'Medical', color: COLORS.accent, icon: 'medical' },
  { value: 'Infrastructure', label: 'Infra', color: '#ffaa00', icon: 'infra' },
  { value: 'Environmental', label: 'Environ', color: '#00aaff', icon: 'environ' },
  { value: 'Unknown', label: 'Unknown', color: '#888', icon: 'unknown' },
];

const SEVERITY_COLORS = ['#4488ff', '#ffcc00', '#ff8800', '#ff4444', '#ff0000'];

export function IncidentCreateModal({
  lat, lng, draft, setDraft, onSubmit, onClose, submitting
}: IncidentCreateModalProps) {
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (draft.title.trim()) onSubmit();
  };

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--color-bg-surface)', borderRadius: '8px', padding: '20px',
          width: '380px', maxWidth: '90vw', border: '1px solid var(--color-border)'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, color: '#fff', fontSize: '16px' }}>Report Incident</h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'var(--color-text-secondary)', fontSize: '18px', cursor: 'pointer'
          }} aria-label="Close"><Icon name="clear" /></button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Type selector */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--color-text-secondary)', marginBottom: '6px' }}>Type</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              {INCIDENT_TYPES.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setDraft(d => ({ ...d, type: t.value }))}
                  style={{
                    flex: 1, padding: '8px 4px', borderRadius: '4px', cursor: 'pointer',
                    fontSize: '11px', fontWeight: draft.type === t.value ? 'bold' : 'normal',
                    backgroundColor: draft.type === t.value ? t.color + '33' : '#2a2a3a',
                    color: draft.type === t.value ? t.color : 'var(--color-text-secondary)',
                    border: `1px solid ${draft.type === t.value ? t.color : '#444'}`,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px'
                  }}
                >
                  <span style={{ fontSize: '16px' }}><Icon name={t.icon} /></span>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Severity selector */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--color-text-secondary)', marginBottom: '6px' }}>Severity</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              {([1, 2, 3, 4, 5] as const).map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setDraft(d => ({ ...d, severity: s }))}
                  style={{
                    flex: 1, padding: '8px', borderRadius: '4px', cursor: 'pointer',
                    fontSize: '14px', fontWeight: 600,
                    backgroundColor: draft.severity === s ? SEVERITY_COLORS[s - 1] + '33' : '#2a2a3a',
                    color: draft.severity === s ? SEVERITY_COLORS[s - 1] : 'var(--color-text-muted)',
                    border: `1px solid ${draft.severity === s ? SEVERITY_COLORS[s - 1] : '#444'}`
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
            <div style={{ fontSize: '10px', color: 'var(--color-text-muted)', marginTop: '4px', textAlign: 'center' }}>
              1 Routine — 5 Override
            </div>
          </div>

          {/* Title */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--color-text-secondary)', marginBottom: '4px' }}>Title *</label>
            <input
              ref={titleRef}
              type="text"
              value={draft.title}
              onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
              placeholder="Brief incident description"
              maxLength={255}
              style={{
                width: '100%', padding: '8px', fontSize: '14px',
                backgroundColor: 'var(--color-bg-elevated)', color: '#fff', border: '1px solid var(--color-border-light)',
                borderRadius: '4px', boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Description (optional, collapsible) */}
          <details style={{ marginBottom: '12px' }}>
            <summary style={{ fontSize: '11px', color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: '4px' }}>
              Description (optional)
            </summary>
            <textarea
              value={draft.description}
              onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
              placeholder="Additional details..."
              rows={3}
              maxLength={5000}
              style={{
                width: '100%', padding: '8px', fontSize: '13px',
                backgroundColor: 'var(--color-bg-elevated)', color: '#fff', border: '1px solid var(--color-border-light)',
                borderRadius: '4px', boxSizing: 'border-box', resize: 'vertical',
                marginTop: '4px'
              }}
            />
          </details>

          {/* Location */}
          <div style={{ marginBottom: '16px', fontSize: '12px', color: 'var(--color-text-secondary)' }}>
            Location: {lat.toFixed(6)}, {lng.toFixed(6)}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || !draft.title.trim()}
            style={{
              width: '100%', padding: '10px', fontSize: '14px', fontWeight: 600,
              backgroundColor: submitting || !draft.title.trim() ? '#444' : 'var(--color-accent)',
              color: '#1a1a1a', border: 'none', borderRadius: '4px',
              cursor: submitting || !draft.title.trim() ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? 'Reporting...' : 'Report Incident'}
          </button>
        </form>
      </div>
    </div>
  );
}
